import { useContext, useEffect, useState } from "react";
import { AppContext } from "../../../../App";
import { displayModalConfirmation } from "../../../../util/displayMessage";
import {
  filterAllPracticesFromAClient,
  groupPractices,
  reducePracticesForGetClient,
} from "../connections.middleware";
import { getLoggedInData } from "../../../../util/auth";
import { sortArrayOfObjectsByStringKey } from "../../../../util/arrays";
import { v4 as uuid } from "uuid";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { useSearchParams } from "react-router-dom";

const useConnections = () => {
  const appContext = useContext(AppContext);
  const [, setSearchParams] = useSearchParams();
  const appData = useContext(AppContext);
  const practices = appData.get("practices") || [];
  const selectedPractices = appData.get("selectedPractices") || [];
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedClientCopy, setSelectedClientCopy] = useState(null);
  const [selectedClientName, setSelectedClientName] = useState("");
  const [allPractices, setAllPractices] = useState([]);
  const [clientId, setClientId] = useState(null);
  const [practiceFilter, setPracticeFilter] = useState("");
  const [localSelectedPractices, setLocalSelectedPractices] = useState([]);
  const [clientItem, setClientItem] = useState(null);
  const [filteredLocalSelectedPractices, setFilteredLocalSelectedPractices] =
    useState([]);
  const {
    setting: { canUseMultiPractice = true },
  } = getLoggedInData();

  useEffect(() => {
    if (selectedPractices.length <= 0) {
      const _allPractices = getAllPractices();
      const firstClient = _allPractices[0];

      const allPracticesSorted = sortArrayOfObjectsByStringKey(
        _allPractices,
        "clientName",
      );
      const firstClientFounded = allPracticesSorted.find(
        (client) => client.clientId === firstClient?.clientId,
      );

      if (firstClientFounded) {
        setClientItem(firstClientFounded);
        setSelectedClientCopy(firstClientFounded);
        setSelectedClient(firstClientFounded);
        setValuesToLocalSelectedPractices(firstClientFounded);
      }
      setAllPractices(allPracticesSorted);

      setSelectedClient(firstClient);
      setClientId(firstClient?.clientId || null);
    }
  }, []);

  useEffect(() => {
    if (selectedPractices.length > 0) {
      const client = getClientFromSelectedPractices(selectedPractices[0]);
      const allClients = getAllPractices();
      const clientSelectedWithPractices =
        allClients.find((c) => c.clientId === client?.clientId) || null;
      setSelectedClient(clientSelectedWithPractices);
      setClientItem(clientSelectedWithPractices);
      setAllPractices(sortArrayOfObjectsByStringKey(allClients, "clientName"));
      setClientId(client?.clientId || null);
      setSelectedClientName(client?.client[0]?.name || "");
      setValuesToLocalSelectedPractices(clientSelectedWithPractices);
    }
  }, [selectedPractices]);

  useEffect(() => {
    setFilteredLocalSelectedPractices(
      filterClientsByPracticeName(practiceFilter),
    );
  }, [practiceFilter, localSelectedPractices]);

  const setValuesToLocalSelectedPractices = (client) => {
    const practicesWithConnectionProperty = client.practices.map((practice) => {
      return {
        ...practice,
        isConnected: selectedPractices.includes(practice._id),
        id: uuid(),
      };
    });

    setLocalSelectedPractices(practicesWithConnectionProperty);
    setFilteredLocalSelectedPractices(practicesWithConnectionProperty);
  };

  const getAllPractices = () => {
    let obj = groupPractices(practices);

    return Object.values(obj);
  };

  const handleClientSelected = (client, isForDiva = false) => {
    isForDiva ? setSelectedClientCopy(client) : setSelectedClient(client);
    setSelectedClientName(client.clientName);
  };

  const activateClient = () => {
    setSelectedClient(selectedClientCopy);
    setValuesToLocalSelectedPractices(selectedClientCopy);
  };

  const handleClickPractice = (practice) => {
    if (checkClientId(practice)) {
      updatePractices(practice);
    } else {
      displayModalConfirmation(
        "Can't select",
        <span>
          Dear user, you have selected practices from another client, you can
          uncheck those practices, and select this new one.
        </span>,
        () => {
          appData.set("selectedPractices", [practice]);
        },
      );
    }
  };

  const updatePractices = (practice) => {
    const exist = selectedPractices.includes(practice);
    if (exist) {
      appData.set(
        "selectedPractices",
        selectedPractices.filter((p) => p !== practice),
      );
    } else {
      const _selectedPractices = canUseMultiPractice
        ? [...selectedPractices, practice]
        : [practice];
      appData.set("selectedPractices", _selectedPractices);
    }
  };

  const handleCheckAllPractices = () => {
    const { clientId: id = null } = selectedClient;
    const _practices = filterAllPracticesFromAClient(practices, id);
    appData.set("selectedPractices", _practices);
  };

  const handleUnCheckAllPractices = () => {
    appData.set("selectedPractices", []);
  };

  const handleUnCheckAllLocalPractices = () => {
    setLocalSelectedPractices((prev) =>
      prev.map((practice) => ({ ...practice, isConnected: false })),
    );
  };

  const handleCheckAllLocalPractices = () => {
    setLocalSelectedPractices((prev) =>
      prev.map((practice) => ({ ...practice, isConnected: true })),
    );
  };

  const checkClientId = (practiceId) => {
    const firstSelectedPractice = selectedPractices
      ? selectedPractices[0]
      : null;
    if (!firstSelectedPractice) return true;

    const clientFromFirstSelected = reducePracticesForGetClient(
      practices,
      firstSelectedPractice,
    );

    const clientFromCurrentPractice = reducePracticesForGetClient(
      practices,
      practiceId,
    );

    return (
      clientFromFirstSelected.clientId === clientFromCurrentPractice.clientId
    );
  };

  const getClientFromSelectedPractices = (practiceId) => {
    const client = reducePracticesForGetClient(practices, practiceId);
    return client || null;
  };

  const getClientIdFromSelectedPractices = (practiceId) => {
    const client = reducePracticesForGetClient(practices, practiceId);
    return client.clientId || null;
  };

  const checkIfPracticeIsSelected = (practiceId) => {
    if (selectedPractices.includes(practiceId)) return 1;

    return 0;
  };

  const handleClickLocalPractice = (practiceId) => {
    const practice = localSelectedPractices.find((p) => p._id === practiceId);
    if (practice) {
      const updatedPractice = {
        ...practice,
        isConnected: !practice.isConnected,
      };
      setLocalSelectedPractices((prev) =>
        prev.map((p) => (p._id === practiceId ? updatedPractice : p)),
      );
    }
  };

  const handleUpdateConnections = () => {
    appData.set(
      "selectedPractices",
      localSelectedPractices.filter((p) => p.isConnected).map((p) => p._id),
    );
    toast.success("Connections updated successfully!");
  };

  function filterClientsByPracticeName(query) {
    if (!query.trim()) {
      return localSelectedPractices;
    }

    const loweredQuery = query.toLowerCase();

    return localSelectedPractices.filter((practice) =>
      practice.name.toLowerCase().includes(loweredQuery),
    );
  }

  const handleClickSetPractices = () => {
    // TODO improve functionality
    if (
      localSelectedPractices.filter((practice) => practice.isConnected)
        ?.length <= 0
    ) {
      Swal.fire({
        title: "Error",
        icon: "error",
        text: "Must select at least one practice.",
      });
      return;
    }
    setSearchParams({});
    window.location.reload();
    appContext.set("selectedPractices", [
      ...localSelectedPractices.filter((p) => p.isConnected).map((p) => p._id),
    ]);
    appContext.set("showPracticeModal", false);
    localStorage.setItem("filters", "");
  };

  return {
    selectedClient,
    allPractices,
    selectedPractices,
    clientId,
    canUseMultiPractice,
    handleClientSelected,
    handleClickPractice,
    handleCheckAllPractices,
    handleUnCheckAllPractices,
    checkIfPracticeIsSelected,
    getClientIdFromSelectedPractices,
    selectedClientName,
    activateClient,
    localSelectedPractices,
    handleClickLocalPractice,
    handleUnCheckAllLocalPractices,
    handleCheckAllLocalPractices,
    handleUpdateConnections,
    practiceFilter,
    setPracticeFilter,
    filteredLocalSelectedPractices,
    handleClickSetPractices,
    clientItem,
    setClientItem,
  };
};

export default useConnections;
